// Line icons on a 24px grid, stroke 1.8, round caps (design system: Iconography). Drawn in currentColor.
// Icons that point (return, back, forward) mirror in right-to-left layouts; object icons never mirror.
const PATHS = {
  plus: 'M12 5v14M5 12h14',
  cash: 'M2 6h20v12H2zM12 9.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5',
  return: 'M9 14 4 9l5-5M4 9h11a5 5 0 0 1 0 10h-3',
  layers: 'm12 3 9 5-9 5-9-5zM3 13l9 5 9-5',
  flag: 'M5 21V4h10l-1.5 4H20v8H9.5l1.5-4H5',
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
  route: 'M8 19h7a3 3 0 0 0 0-6H9a3 3 0 0 1 0-6h7M6 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4M18 3a2 2 0 1 0 0 4 2 2 0 0 0 0-4',
  swap: 'M4 8h14l-3-3M20 16H6l3 3',
  more: 'M5 10.4a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2M12 10.4a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2M19 10.4a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2',
  check: 'M20 6 9 17l-5-5',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 7v5l3 2',
  alert: 'M12 3 2 21h20zM12 10v5M12 18h.01',
  info: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 11v5M12 8h.01',
  truck: 'M3 6h11v10H3zM14 9h4l3 3v4h-7M7 15a2 2 0 1 0 0 4 2 2 0 0 0 0-4M17 15a2 2 0 1 0 0 4 2 2 0 0 0 0-4',
  box: 'M21 8 12 3 3 8v8l9 5 9-5zM3 8l9 5 9-5M12 13v8',
  cart: 'M3 4h2l2 12h12l2-8H6M9 18.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M17 18.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3',
  tag: 'M3 12V3h9l9 9-9 9zM7.5 6a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3',
  users: 'M9 4.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7M2 21a7 7 0 0 1 14 0M16 4a3.5 3.5 0 0 1 0 7M22 21a6 6 0 0 0-4-5.6',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  gear: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1',
  file: 'M14 3H6v18h12V7zM14 3v4h4',
  handshake: 'M3 12l4-4 5 3 5-3 4 4-9 7z',
  menu: 'M4 6h16M4 12h16M4 18h16',
  wifiOff: 'M2 2l20 20M8.5 16.5a5 5 0 0 1 7 0M5 12.6a10 10 0 0 1 5-2.5M19 12.6a10 10 0 0 0-2.6-1.9M12 20h.01',
  download: 'M12 3v12M7 10l5 5 5-5M4 21h16',
  close: 'M6 6l12 12M18 6 6 18',
  chevron: 'm9 6 6 6-6 6',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14M21 21l-5-5',
  scan: 'M3 7V4h3M18 4h3v3M21 17v3h-3M6 20H3v-3M7 8v8M10 8v8M13 8v8M17 8v8',
  back: 'M19 12H5M12 19l-7-7 7-7',
  folder: 'M3 6h6l2 2h10v11H3z',
  phone: 'M5 3h4l2 5-3 2a11 11 0 0 0 6 6l2-3 5 2v4a2 2 0 0 1-2 2A17 17 0 0 1 3 5a2 2 0 0 1 2-2',
} as const;

export type IconName = keyof typeof PATHS;
const DIRECTIONAL: ReadonlySet<IconName> = new Set<IconName>(['return', 'chevron', 'back']);

export function Icon({ name, className }: { name: IconName; className?: string }) {
  const cls = ['yv-icon', DIRECTIONAL.has(name) ? 'yv-icon--dir' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <svg
      className={cls}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
